import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";
import {
  collection,
  query,
  getDocs,
  doc,
  getDoc,
  where,
  orderBy,
} from "firebase/firestore";
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Divider,
  Avatar,
  Badge,
  CircularProgress,
  Paper,
  Stack,
  Alert,
  Button,
} from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import { useChatNotifications } from "./ChatNotifications";
import { isLoggedIn } from "../session";
import BottomNav from "./BottomNav";
import EmptyState from "./EmptyState";
import { PageShell, PageHeader, PageContent } from "./Page";

function Chats() {
  const [chats, setChats] = useState([]);
  const [authUserId, setAuthUserId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { unreadByChat, totalUnread, permission, requestPermission } = useChatNotifications();

  const fetchAuthUserId = useCallback(async () => {
    try {
      const user = await isLoggedIn();
      if (user) {
        setAuthUserId(user.uid);
      } else {
        navigate("/login");
      }
    } catch (error) {
      setError("Failed to authenticate user.");
    }
  }, [navigate]);

  useEffect(() => {
    fetchAuthUserId();
  }, [fetchAuthUserId]);

  const fetchChats = useCallback(async () => {
    if (!authUserId) return;

    setLoading(true);
    setError(null);

    try {
      const chatsRef = collection(db, "chats");
      const chatsQuery = query(
        chatsRef,
        where("participants", "array-contains", authUserId),
        orderBy("lastMessageTime", "desc")
      );
      const chatsSnapshot = await getDocs(chatsQuery);

      const chatsData = await Promise.all(chatsSnapshot.docs.map(async (chatDoc) => {
        const chatData = chatDoc.data();
        const otherUserId = chatData.participants.find(id => id !== authUserId);
        const carDoc = await getDoc(doc(db, "cars", chatData.carId));
        const carData = carDoc.data();
        const otherUserDoc = await getDoc(doc(db, "publicProfiles", otherUserId));
        const otherUserData = otherUserDoc.data() || {};

        return {
          id: chatDoc.id,
          carId: chatData.carId,
          carTitle: carData?.title || "Publicación eliminada",
          otherUserName: otherUserData.displayName || "Usuario",
          otherUserAvatar: otherUserData.photoURL,
          lastMessage: chatData.lastMessage,
          lastMessageTime: chatData.lastMessageTime.toDate(),
        };
      }));

      setChats(chatsData);
    } catch (error) {
      setError("Failed to load chats. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [authUserId]);

  useEffect(() => {
    if (authUserId) {
      fetchChats();
    }
  }, [authUserId, fetchChats, totalUnread]);

  const handleChatClick = (chatId, carId) => {
    navigate(`/chatbox/${chatId}/${carId}`);
  };

  // Función para formatear la fecha sin usar date-fns
  const formatDate = (date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <PageShell>
      <PageHeader title="Chats" subtitle="Tus conversaciones con compradores y vendedores" />
      <PageContent>
        {permission === "default" && (
          <Alert
            icon={<NotificationsActiveOutlinedIcon />}
            severity="info"
            sx={{ mb: 3, borderRadius: 3, alignItems: "center" }}
            action={
              <Button color="inherit" size="small" onClick={requestPermission} sx={{ fontWeight: 700 }}>
                Activar
              </Button>
            }
          >
            Activá las notificaciones para enterarte de los mensajes nuevos aunque estés en otra pestaña.
          </Alert>
        )}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
            <CircularProgress color="secondary" />
          </Box>
        ) : error ? (
          <Typography color="error" sx={{ mt: 2, textAlign: "center" }}>
            {error}
          </Typography>
        ) : chats.length > 0 ? (
          <Paper elevation={0} sx={{ borderRadius: 4, border: 1, borderColor: "divider", overflow: "hidden" }}>
            <List disablePadding>
              {chats.map((chat, index) => (
                <React.Fragment key={chat.id}>
                  {index > 0 && <Divider component="li" />}
                  <ListItemButton
                    onClick={() => handleChatClick(chat.id, chat.carId)}
                    sx={{ px: { xs: 2, md: 3 }, py: 2, alignItems: "flex-start" }}
                  >
                    <ListItemAvatar>
                      <Badge
                        color="secondary"
                        badgeContent={unreadByChat[chat.id] || 0}
                        invisible={!unreadByChat[chat.id]}
                      >
                        <Avatar
                          src={chat.otherUserAvatar}
                          alt={chat.otherUserName}
                          sx={{ width: 44, height: 44, bgcolor: "primary.light" }}
                        />
                      </Badge>
                    </ListItemAvatar>
                    <ListItemText
                      sx={{ ml: 1, my: 0 }}
                      primary={
                        <Stack direction="row" justifyContent="space-between" spacing={2}>
                          <Typography variant="subtitle1" noWrap sx={{ fontWeight: unreadByChat[chat.id] ? 800 : 700 }}>
                            {chat.carTitle}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, pt: 0.5 }}>
                            {formatDate(chat.lastMessageTime)}
                          </Typography>
                        </Stack>
                      }
                      secondary={
                        <Typography component="span" variant="body2" color="text.secondary" noWrap sx={{ display: "block" }}>
                          <Box component="span" sx={{ color: "text.primary", fontWeight: 600 }}>
                            {chat.otherUserName}:
                          </Box>{" "}
                          {chat.lastMessage || "Sin mensajes todavía"}
                        </Typography>
                      }
                    />
                  </ListItemButton>
                </React.Fragment>
              ))}
            </List>
          </Paper>
        ) : (
          <EmptyState
            icon={<ChatBubbleOutlineIcon />}
            title="No hay chats todavía"
            text="Cuando consultes por un vehículo, la conversación aparecerá acá."
          />
        )}
      </PageContent>
      <BottomNav />
    </PageShell>
  );
}

export default Chats;
